import React, { Fragment, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { CircularProgress, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useFormikContext } from 'formik'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { InfoContainer } from '../shared/InfoContainer'
import { Button } from '../shared/Button'
import RecollectIcon from 'src/assets/icons/recollect.svg'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ValidateUSDOTNumber } from 'src/constants/actionTypes'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { DOTNumberFormValues } from './DOTNumberForm.interface'
import { ValidateUSDOTNumberResponse } from 'src/interfaces/usDOTNumber'

interface Props {
  handleChange: (e: React.ChangeEvent<any>) => void
  setIsUSDOTNumberReadyForUpload: (isReady: boolean) => void
}

const DATE_MASK = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
const USDOT_NUMBER_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]

export const USDOTNumberContent = ({ handleChange, setIsUSDOTNumberReadyForUpload }: Props) => {
  const dispatch = useDispatch()
  const currentStep = useSelector(getCurrentCollectDataStep)
  const { values, resetForm, validateField, setFieldTouched, errors, touched } =
    useFormikContext<DOTNumberFormValues>()

  const [isUSDOTNumberChecked, setIsUSDOTNumberChecked] = useState<boolean>(false)
  const [isUSDOTNumberValid, setIsUSDOTNumberValid] = useState<boolean>(true)
  const [isUSDOTNumberValidationProcessing, setUSDOTNumberValidationProcessing] =
    useState<boolean>(false)

  const validateUSDOTNumber = useCallback(
    async (usdotNumber: string) => {
      setUSDOTNumberValidationProcessing(true)
      setIsUSDOTNumberChecked(true)

      try {
        const response: ValidateUSDOTNumberResponse = await createAsyncAction(
          dispatch,
          ValidateUSDOTNumber.request({
            usDotNumber: usdotNumber,
            stepId: currentStep?.id,
          }),
        )
        setIsUSDOTNumberValid(response.isValid)
        setIsUSDOTNumberReadyForUpload(response.isValid)
      } catch (err) {
        setIsUSDOTNumberValid(false)
        setIsUSDOTNumberReadyForUpload(false)
      } finally {
        setUSDOTNumberValidationProcessing(false)
      }
    },
    [currentStep?.id, dispatch],
  )

  const handleReset = () => {
    setIsUSDOTNumberValid(true)
    setIsUSDOTNumberChecked(false)
    setIsUSDOTNumberReadyForUpload(false)
    resetForm({
      values: {
        ...values,
        usDotNumber: '',
        lastSubmittedAt: '',
        file: undefined,
      },
    })
  }

  return (
    <Fragment>
      <div css={tw`mb-4`}>
        <FormikTextField
          name="usDotNumber"
          placeholder="Enter USDOT number"
          label="USDOT Number"
          mask={USDOT_NUMBER_MASK}
          onChange={handleChange}
          css={tw`w-full`}
          disabled={isUSDOTNumberChecked}
          inputProps={{ 'data-testid': 'contractors-onboarding-usdot-number-input' }}
        />
      </div>

      <div css={tw`mb-4`}>
        <FormikTextField
          name="lastSubmittedAt"
          placeholder="Enter date"
          label="Date of last submitted MCS-150 (Motor Carrier Identification Report)"
          mask={DATE_MASK}
          onChange={handleChange}
          css={tw`w-full`}
          inputProps={{ 'data-testid': 'contractors-onboarding-usdot-date-input' }}
        />
      </div>

      <div>
        {!isUSDOTNumberChecked && (
          <Button
            className={tw`w-full mt-4`}
            data-testid="contractors-onboarding-usdot-check-btn"
            onClick={async () => {
              // Mark fields as touched to show validation errors
              setFieldTouched('usDotNumber', true)
              setFieldTouched('lastSubmittedAt', true)

              // Validate the fields
              await validateField('usDotNumber')
              await validateField('lastSubmittedAt')

              // Check if there are any errors after validation
              const hasUsDotNumberError = !!(errors.usDotNumber && touched.usDotNumber)
              const hasLastSubmittedAtError = !!(errors.lastSubmittedAt && touched.lastSubmittedAt)

              // Only proceed if no validation errors
              if (!hasUsDotNumberError && !hasLastSubmittedAtError && values.usDotNumber) {
                validateUSDOTNumber(values.usDotNumber)
              }
            }}
          >
            Check USDOT number
          </Button>
        )}
        {!isUSDOTNumberValid && isUSDOTNumberChecked && (
          <InfoContainer color="error" className={tw`mt-4`}>
            We couldn't verify your USDOT Number. Please reset and try again.
          </InfoContainer>
        )}
        {isUSDOTNumberValid && isUSDOTNumberChecked && (
          <InfoContainer color="success" className={tw`mt-4`}>
            USDOT Number has been verified. You can submit it now.
          </InfoContainer>
        )}
        {isUSDOTNumberChecked && (
          <Button
            className={tw`w-full mt-4`}
            onClick={handleReset}
            color="grey"
            startIcon={<img src={RecollectIcon} />}
            data-testid="contractors-onboarding-usdot-reset-btn"
          >
            Reset and Try Again
          </Button>
        )}
      </div>

      {isUSDOTNumberValidationProcessing && (
        <div
          css={tw`absolute top-0 left-0 w-full h-full bg-[rgba(255, 255, 255, 0.95)] flex flex-col items-center justify-center px-10`}
        >
          <CircularProgress size={22} />
          <Typography css={tw`text-center mt-4 text-primary-500 font-normal text-base`}>
            Looking for your USDOT Number info... Please wait while we process your vechicle
            information.
          </Typography>
        </div>
      )}
    </Fragment>
  )
}
